<template>
    <div ref="container" class="xx-level-select">
        <el-popover
            ref="popover"
            :popper-class="`xx-level-select-popper ${popperClass}`"
            placement="bottom-start"
            trigger="manual"
            v-model="popoverVisible"
            v-bind="$attrs"
            v-on="popperListeners"
        >
            <div ref="wrapper" v-if="popoverVisible">
                <select-wrapper ref="selectWrapper" v-if="canShowSelect"></select-wrapper>
                <view-wrapper ref="viewWrapper" v-else></view-wrapper>
                <div v-if="multiple && !innerDisabled" class="footer">
                    <xx-link
                        class="footer-button"
                        :class="{ 'pull-right': !canShowSelect }"
                        size="small"
                        @click="showSelect = !showSelect"
                    >{{ canShowSelect ? "查看已选" : "返回" }}</xx-link>
                </div>
            </div>
            <div class="no-outline" slot="reference">
                <input-view ref="inputView"></input-view>
            </div>
        </el-popover>
    </div>
</template>
<script>
import emitter from 'element-ui/lib/mixins/emitter';
import { deepClone } from '@lsfe/tools';
import SelectWrapper from './SelectWrapper';
import ViewWrapper from './ViewWrapper';
import InputView from './inputView';
import LevelStore from './model/levelStore';
import { isEqual } from '../../utils/tools';

export default {
    name: 'XxLevelSelect',
    components: {
        SelectWrapper,
        ViewWrapper,
        InputView
    },
    mixins: [emitter],
    props: {
        value: {
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        size: {
            type: String
        },
        data: {
            type: Array,
            required: true
        },
        multiple: {
            type: Boolean,
            default: false
        },
        showCount: {
            type: Boolean,
            default: true
        },
        showSelectTabAll: {
            type: Boolean,
            default: true
        },
        showSelectAll: {
            type: Boolean,
            default: true
        },
        placeholder: {
            type: String,
            default: '请选择'
        },
        filterPlaceholder: {
            type: String,
            default: '请搜索'
        },
        tabConfig: {
            type: [Array, Boolean],
            default: () => []
        },
        filterInAll: {
            type: Boolean,
            default: true
        },
        valueKey: {
            type: String,
            default: 'id'
        },
        renderNode: {
            type: Function
        },
        renderViewNode: {
            type: Function
        },
        popperClass: {
            type: String,
            default: ''
        },
        clearable: {
            type: Boolean,
            default: true
        },
        closePopperAfterSelected: {
            type: Boolean,
            default: true
        },
        showTags: [Boolean, Number],
        maxSelectedCount: {
            type: Number,
            default: Infinity
        },
        maxFilterLength: {
            type: Number,
            default: 100
        },
        allLevelLazyLoad: {
            type: Boolean,
            default: false
        },
        useScrollAllData:{
            type: Boolean,
            default: false
        }
    },
    provide() {
        return {
            levelSelect: this
        };
    },
    inject: {
        elForm: {
            default: ''
        },
        elFormItem: {
            default: ''
        }
    },
    data() {
        return {
            popoverVisible: false,
            shouldTrigger: false,
            showSelect: true,
            store: null,
            storeValue: this.value && deepClone(this.value),
            startClickElement: {}
        };
    },
    computed: {
        popperListeners() {
            const listeners = ['show', 'after-enter', 'hide', 'after-leave'];
            const result = {};
            listeners.forEach(item => {
                if (this.$listeners[item]) {
                    result[item] = this.$listeners[item];
                }
            });
            return result;
        },
        selectSize() {
            return this.size || (this.elFormItem || {}).elFormItemSize;
        },
        innerDisabled() {
            return this.disabled || (this.elForm || {}).disabled;
        },
        canShowSelect() {
            return !this.multiple || (!((this.disabled || (this.elForm || {}).disabled)) && this.showSelect);
        }
    },
    watch: {
        data: {
            handler(val) {
                this.store.init(val);
            },
            deep: true
        },
        value: {
            handler(val) {
                let value = val;
                if (!this.multiple) {
                    value = [val];
                }
                if (!isEqual(value, this.storeValue)) {
                    // 先将数据清空
                    this.storeValue = this.value && deepClone(value);
                    // const {selectedValue} = this.store;
                    // selectedValue.forEach(item => {
                    //     this.store.setSelectedCount(false, item);
                    // });
                    // this.store.setSelectedCount();
                    // 重新设置数据并计算选中数量
                    this.store.setValue();
                    this.store.initCount(); // 设置选项个数和选中个数
                    this.dispatch('ElFormItem', 'el.form.change', this.innerValue);
                }
            },
            deep: true
        },
        'store.selectedValueMap': {
            handler() {
                this.store.setSelectedValue();
            },
            deep: true
        }
    },
    created() {
        this.store = new LevelStore(this.data, { vm: this });
    },
    mounted() {
        // 事件监听，点击关闭下拉框
        window.addEventListener('mousedown', this.handleBeforeClick);
        window.addEventListener('mouseup', this.triggerClick);
    },
    beforeDestroy() {
        window.removeEventListener('mousedown', this.handleBeforeClick);
        window.removeEventListener('mouseup', this.triggerClick);
    },
    methods: {
        handleBeforeClick(e) {
            this.startClickElement = e;
        },
        triggerClick(e) {
            if (!this.popoverVisible && !this.innerDisabled) {
                return;
            }
            const containerEl = this.$refs.container;
            const wrapperEl = this.$refs.wrapper;
            if (
                containerEl && wrapperEl &&
                !containerEl.contains(this.startClickElement.target) &&
                !wrapperEl.contains(this.startClickElement.target) &&
                !containerEl.contains(e.target) &&
                !wrapperEl.contains(e.target)
            ) {
                // shouldTrigger防止在点击其他地方时重复触发下面方法
                if (this.shouldTrigger) {
                    this.popoverVisible = false;
                    this.dispatch('ElFormItem', 'el.form.blur', this.innerValue);
                }
                this.shouldTrigger = false;
            } else {
                this.shouldTrigger = true;
            }
        },
        emitInit() {
            this.$nextTick(() => {
                const { value, items } = this.store.getEmitValue();
                this.$emit('data-update', value, items, deepClone(this.store.initialData));
            });
        },
        emitChange() {
            const { value, items } = this.store.getEmitValue();
            this.storeValue = deepClone(value);
            this.$emit('input', value);
            this.$emit('change', value, items);
            this.dispatch('ElFormItem', 'el.form.change', value);
        },
        emitTabChange(item) {
            this.$emit('tab-change', item);
        },
        emitCheck(val, item) {
            this.$emit('check', val, item);
        },
        closePopper() {
            this.popoverVisible = false;
        },
        getSelectedValue() {
            const { value, items } = this.store.getEmitValue();
            return {value, data: deepClone(items)};
        }
    }
};
</script>
<style lang="scss">
.xx-level-select-popper {
    padding: 10px 0 !important;
    .footer-button {
        padding-bottom: 0;
        margin: 0 20px;
    }
}
</style>
<style lang="scss">
.xx-level-select {
    display: inline-block;
    .no-outline {
        outline: none;
    }
}
</style>
